<template>
  <v-text-field
    ref="inputRef"
    v-bind="$props"
    v-model="formattedValue"
    :error-messages="localesErrorMessages"
    :label="label ? $t(label) : label"
    :placeholder="placeholder ? $t(placeholder) : placeholder"
    :validation-value="numberValue"
    :rules="rules"
    :class="{ 'centered-align': horizontal }"
  >
    <template v-if="horizontal && !hideBtns" v-slot:prepend-inner>
      <v-icon
        icon="mdi-minus"
        class="cursor-pointer"
        :class="{ 'invisible-icon': numberValue <= min }"
        size="small"
        @click="minusOne"
        :disabled="numberValue <= min"/>
    </template>

    <template v-if="horizontal && !hideBtns" v-slot:append-inner>
      <v-icon
        icon="mdi-plus"
        class="cursor-pointer"
        :class="{ 'invisible-icon': numberValue >= max }"
        size="small"
        @click="plusOne"
        :disabled="numberValue >= max"
      />
    </template>

    <template v-else-if="!hideBtns" v-slot:append-inner>
      <div class="icon-wrapper">
        <v-icon icon="mdi-chevron-up" @click="plusOne" :disabled="numberValue >= max"/>
        <v-icon icon="mdi-chevron-down" @click="minusOne" :disabled="numberValue <= min"/>
      </div>
    </template>
  </v-text-field>
</template>

<script setup>
import { computed, watch } from 'vue';
import { useCurrencyInput } from 'vue-currency-input';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  placeholder: String,
  label: String,
  errorMessages: String|Array,
  modelValue: Number,
  rules: Array,
  precision: {
    type: Number,
    default: 2
  },
  variant: {
    type: String,
    default: 'solo'
  },
  hideDetails: {
    type: String,
    default: 'auto'
  },
  hideBtns: {
    type: Boolean,
    default: false
  },
  flat: {
    type: Boolean,
    default: true
  },
  rounded: {
    type: Number,
    default: 9
  },
  horizontal: {
    type: Boolean,
    default: false
  },
  max: {
    type: Number,
    default: 999_999.99
  },
  min: {
    type: Number,
    default: 0.01
  },
});

const emit = defineEmits(['update:modelValue']);

const options = {
  locale: 'RU',
  currency: 'USD',
  currencyDisplay: "hidden",
  precision: props.precision,
  distractionFree: false,
  valueRange: {
    min: 0
  }
};

const { t } = useI18n();
const { inputRef, formattedValue, numberValue, setValue, setOptions } = useCurrencyInput(options, false);

const plusOne = () => setValue(numberValue.value + 1);
const minusOne = () => setValue(numberValue.value - 1);

const localesErrorMessages = computed(() => {
  if (Array.isArray(props.errorMessages)) {
    const errors = [];

    props.errorMessages.forEach(error => {
      errors.push(error ? t(error) : error)
    })

    return errors;
  }

  return props.errorMessages ? t(props.errorMessages) : props.errorMessages
})

defineExpose({
  resetValidation: () => {
    inputRef.value.resetValidation();
  },
  reset: () => {
    setValue(null);
    inputRef.value.reset();
  },
});

watch(() => props.modelValue,
  (value) => {
    setValue(value);
  }
);

watch(() => props.precision,
  (value) => {
    setOptions({precision: value})
  }
);

watch(numberValue, (newValue) => {
  if (newValue === null) {
    formattedValue.value = '';
    emit('update:modelValue', null);
    return;
  }

  if (newValue > props.max) {
    numberValue.value = props.max;
  } else if (newValue < props.min) {
    numberValue.value = props.min;
  }
  emit('update:modelValue', newValue);
});

</script>

<style scoped lang="scss">
:deep(.v-field) {
  border-radius: 9px;
}

:deep(.v-field__input) {
  font-style: normal;
  font-weight: 500;
  font-size: 16px;
  line-height: 22px;
  color: rgb(var(--input-text-color));
}

.centered-align {
  :deep(.v-field__input) {
    text-align: center;
  }

  :deep(.v-icon) {
    color: rgb(var(--black-color));
  }

  :deep(.invisible-icon) {
    opacity: 0;
    pointer-events: none;
  }
}

:deep(.v-field__outline) {
  border: 1px solid rgb(var(--border-color));
  border-radius: 8px;
  color: rgb(var(--boulder-color));
}

:deep(.v-field--error:not(.v-field--disabled) .v-field__outline) {
  border: 1px solid rgb(var(--error-color));
}

:deep(.v-input__details) {
  font-style: normal;
  font-weight: 500;
  font-size: 14px;
  line-height: 19px;
  padding-inline: 0;
}

:deep(.v-field-label--floating) {
  opacity: 1;
}

:deep(.v-field--variant-solo) {
  color: rgb(var(--gunsmoke-color));
}

:deep(.v-field--variant-solo.v-field--dirty) {
  color: rgb(var(--input-text-color));
}


/* Отступы для иконок по макету */
.icon-wrapper > i {
  margin-top: -6px;
}

.icon-wrapper > i:first-child {
  margin-top: 0;
  margin-bottom: -6px;
}
</style>
